/* =============================================================
   New Zealand Study & Migration Page — United Dream Mongolia
   Design: Clean professional with NZ green/blue tones
   ============================================================= */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  ChevronDown, ChevronUp, ArrowLeft, MapPin, GraduationCap,
  DollarSign, FileText, Users, Briefcase, Home, TrendingUp,
  CheckCircle, AlertCircle, Globe, Clock, Star, Building2
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useConsultation } from "@/contexts/ConsultationModalContext";

/* ── Accordion helper ── */
function Accordion({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden mb-3">
      <button
        className="w-full flex items-center justify-between px-5 py-4 bg-white/5 hover:bg-white/10 transition-colors text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="text-white font-semibold text-sm md:text-base">{title}</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#00A86B] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-white/50 flex-shrink-0" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 py-4 bg-white/[0.02] text-white/80 text-sm leading-relaxed">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Section Tab ── */
const TABS = [
  { id: "tuition", label: "Сургалтын төлбөр", icon: DollarSign },
  { id: "visa-fees", label: "Визний хураамж", icon: FileText },
  { id: "schools", label: "Сургуулиуд", icon: Building2 },
  { id: "bank", label: "Банкны баталгаа", icon: DollarSign },
  { id: "documents", label: "Бүрдүүлэх материал", icon: FileText },
  { id: "family", label: "Гэр бүлээрээ", icon: Users },
  { id: "work", label: "Ажиллах эрх", icon: Briefcase },
  { id: "children", label: "Хүүхэд дагалдуулах", icon: Users },
  { id: "living", label: "Амьдрах зардал", icon: Home },
  { id: "residence", label: "Байнгын оршин суух", icon: TrendingUp },
];

export default function NewZealandPage() {
  const { openConsultation } = useConsultation();
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState("tuition");
  const [showApplyForm, setShowApplyForm] = useState(false);

  // Application form state
  const [appForm, setAppForm] = useState({
    studentName: "", gender: "", dob: "", telephone: "", mobile: "",
    passportNumber: "", passportStartDate: "", passportExpiryDate: "",
    residentialAddress: "", email: "",
    kinName: "", kinRelationship: "", kinAddress: "", kinMobile: "", kinEmail: "",
    englishExamType: "IELTS", englishReading: "", englishWriting: "",
    englishListening: "", englishSpeaking: "", englishOverall: "",
  });
  const [education, setEducation] = useState([{ year: "", institution: "", programme: "", gpa: "" }]);
  const [workExp, setWorkExp] = useState([{ startEnd: "", organisation: "", jobTitle: "", duties: "" }]);
  const [referees, setReferees] = useState([{ name: "", title: "", organisation: "", contact: "", mobile: "", email: "" }, { name: "", title: "", organisation: "", contact: "", mobile: "", email: "" }]);
  const [prefInst, setPrefInst] = useState([{ institution: "", programme: "", startDate: "" }, { institution: "", programme: "", startDate: "" }, { institution: "", programme: "", startDate: "" }]);
  const [docChecks, setDocChecks] = useState<Record<string, boolean>>({
    passport: false, transcript: false, cv: false, sop: false, english: false, emergency: false,
  });
  const [appSubmitted, setAppSubmitted] = useState(false);

  const submitApp = trpc.nz.submitApplication.useMutation({
    onSuccess: () => {
      setAppSubmitted(true);
      toast.success("Таны анкет хүлээн авагдлаа. Бид тантай удахгүй холбогдох болно.");
    },
    onError: (err) => {
      toast.error(err.message ?? "Алдаа гарлаа. Дахин оролдоно уу.");
    },
  });

  const handleAppSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitApp.mutate({
      ...appForm,
      educationBackground: JSON.stringify(education),
      workExperience: JSON.stringify(workExp),
      referees: JSON.stringify(referees),
      preferredInstitutions: JSON.stringify(prefInst),
      documents: JSON.stringify(docChecks),
    });
  };

  return (
    <div className="min-h-screen bg-[#0A1628]">
      <Header />

      {/* Hero */}
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00A86B]/10 via-[#0A1628] to-[#1E6FD9]/10" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#00A86B]/5 rounded-full blur-3xl" />
        <div className="container relative">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Нүүр хуудас руу буцах
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="text-5xl">🇳🇿</div>
            <div>
              <div className="text-[#00A86B] text-sm font-semibold tracking-widest uppercase mb-1">Шинэ Зеланд</div>
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight">
                Шинэ Зеландад<br />
                <span className="text-[#00A86B]">Суралцах & Амьдрах</span>
              </h1>
            </div>
          </div>

          <p className="text-white/70 text-lg max-w-2xl mb-8">
            Дэлхийн топ боловсролын тогтолцоо, аюулгүй орчин, цэвэр байгаль — Шинэ Зеланд нь суралцаад байнгын оршин суух эрх авах хамгийн тохиромжтой улсуудын нэг.
          </p>

          {/* Key stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Дэлхийн зэрэглэл", value: "#65", sub: "Оклендын их сургууль" },
              { label: "Ажиллах эрх", value: "25 цаг", sub: "7 хоногт (дээд боловсрол)" },
              { label: "Амьдрах зардал", value: "NZD 20K+", sub: "Жилд (INZ шаардлага)" },
              { label: "Байнгын оршин суух", value: "3-5 жил", sub: "Суралцаад авах боломжтой" },
            ].map((s) => (
              <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="text-2xl font-black text-[#00A86B]">{s.value}</div>
                <div className="text-white text-sm font-semibold mt-1">{s.label}</div>
                <div className="text-white/50 text-xs mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Prominent Apply CTA */}
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <button
              onClick={() => setShowApplyForm(true)}
              className="group relative flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-200 hover:scale-105 hover:shadow-2xl hover:shadow-[#00A86B]/40 active:scale-100"
              style={{
                background: "linear-gradient(135deg, #00A86B 0%, #00C97E 50%, #00A86B 100%)",
                boxShadow: "0 4px 24px rgba(0,168,107,0.45), inset 0 1px 0 rgba(255,255,255,0.2)",
                color: "#fff",
              }}
            >
              <span className="text-xl">📋</span>
              <span>Анкет бөглөх</span>
              <span className="text-white/70 text-sm font-normal">/ Fill Application</span>
              <span
                className="absolute -top-2 -right-2 text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse"
                style={{ background: "#FF6B35", color: "#fff" }}
              >
                Шинэ
              </span>
            </button>
            <div className="text-white/50 text-sm self-center">
              Манай мэргэжилтнүүд анкет бөглөөд бид тантай холбогдоно.  Буцаад төлбөргүй.
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="container mb-8">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-amber-200/90 text-sm leading-relaxed">
            <strong>Анхааруулга:</strong> Энэхүү мэдээлэл нь ерөнхий мэдээллийн зорилгоор бэлтгэгдсэн бөгөөд визний баталгаа биш. Визний шийдвэр нь тухайн хүний нөхцөл байдлаас хамаарна. Мэдүүлэхийн өмнө манай мэргэжилтэнтэй заавал зөвлөлдөнө үү.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="container mb-8">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  activeTab === tab.id
                    ? "bg-[#00A86B] text-white shadow-lg shadow-[#00A86B]/30"
                    : "bg-white/5 text-white/60 hover:text-white hover:bg-white/10 border border-white/10"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="container pb-20">
        <AnimatePresence mode="sync">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >

            {/* ── 1. СУРГАЛТЫН ТӨЛБӨР ── */}
            {activeTab === "tuition" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Сургалтын төлбөрийн мэдээлэл</h2>

                {/* General tuition table */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-[#00A86B]/20 border-b border-white/10">
                    <h3 className="text-white font-bold">Ерөнхий сургалтын төлбөр (NZD, жилд)</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left px-5 py-3 text-white/60 font-medium">Сургалтын төрөл</th>
                          <th className="text-right px-5 py-3 text-white/60 font-medium">Жилийн төлбөр (NZD)</th>
                          <th className="text-left px-5 py-3 text-white/60 font-medium hidden md:table-cell">Тэмдэглэл</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { type: "Хэлний курс (English)", fee: "350–450/долоо хоног", note: "1–48 долоо хоног", highlight: true },
                          { type: "Бага, дунд сургууль (Улсын)", fee: "15,000–25,000", note: "Байршил, жилийн түвшнээс хамаарна" },
                          { type: "Бага, дунд сургууль (Хувийн)", fee: "35,000–50,000", note: "Сургалтын хөтөлбөр, тоног төхөөрөмжөөс хамаарна" },
                          { type: "Foundation / Bridging хөтөлбөр", fee: "18,000–30,000", note: "Их сургуульд орох бэлтгэл" },
                          { type: "Гэрчилгээ / Диплом (Level 3–4)", fee: "15,000–23,000", note: "1–2 жилийн хөтөлбөр" },
                          { type: "Бакалавр (Level 7, 3 жил)", fee: "20,000–35,000", note: "3 жилийн бакалавр = 3 жилийн ажлын виз", highlight: true },
                          { type: "Магистр (Level 9, 1–2 жил)", fee: "38,000–65,000", note: "1–2 жилийн магистр = 3 жилийн ажлын виз", highlight: true },
                          { type: "Докторантур (PhD)", fee: "8,500–10,000", note: "Дотоодын оюутантай адил төлбөр" },
                        ].map((row) => (
                          <tr key={row.type} className={`border-b border-white/5 ${row.highlight ? "bg-[#00A86B]/5" : ""}`}>
                            <td className="px-5 py-3 text-white font-medium">{row.type}</td>
                            <td className="px-5 py-3 text-[#00A86B] font-bold text-right">{row.fee}</td>
                            <td className="px-5 py-3 text-white/50 hidden md:table-cell">{row.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ICL Promotion */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-blue-500/20 border-b border-white/10 flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-400" />
                    <h3 className="text-white font-bold">ICL (International College of Language) — Хэлний курс урамшуулал</h3>
                  </div>
                  <div className="p-5">
                    <p className="text-white/60 text-sm mb-4">Communication English / IELTS Бэлтгэл / PTE Бэлтгэл</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left px-3 py-2 text-white/60">Хугацаа</th>
                            <th className="text-right px-3 py-2 text-white/60">Анхны үнэ</th>
                            <th className="text-right px-3 py-2 text-[#00A86B]">Offshore урамшуулал</th>
                            <th className="text-right px-3 py-2 text-blue-400">Onshore урамшуулал</th>
                            <th className="text-left px-3 py-2 text-white/60 hidden md:table-cell">Нэмэлт урамшуулал</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { weeks: "1–12 долоо хоног", orig: "$485/долоо хоног", off: "$295/долоо хоног", on: "$250/долоо хоног", extra: "10+2: 10 долоо хоног авбал 2 долоо хоног үнэгүй" },
                            { weeks: "13–21 долоо хоног", orig: "$485/долоо хоног", off: "$280/долоо хоног", on: "$250/долоо хоног", extra: "13+1: 13 долоо хоног авбал 1 долоо хоног үнэгүй" },
                            { weeks: "22–32 долоо хоног", orig: "$485/долоо хоног", off: "$260/долоо хоног", on: "$250/долоо хоног", extra: "22+2: 22 долоо хоног авбал 2 долоо хоног үнэгүй" },
                            { weeks: "33+ долоо хоног", orig: "$485/долоо хоног", off: "$250/долоо хоног", on: "$250/долоо хоног", extra: "33+3: 33 долоо хоног авбал 3 долоо хоног үнэгүй" },
                          ].map((row) => (
                            <tr key={row.weeks} className="border-b border-white/5">
                              <td className="px-3 py-3 text-white font-medium">{row.weeks}</td>
                              <td className="px-3 py-3 text-white/40 line-through text-right">{row.orig}</td>
                              <td className="px-3 py-3 text-[#00A86B] font-bold text-right">{row.off}</td>
                              <td className="px-3 py-3 text-blue-400 font-bold text-right">{row.on}</td>
                              <td className="px-3 py-3 text-white/50 text-xs hidden md:table-cell">{row.extra}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3 flex gap-4 text-xs text-white/40">
                      <span>Бүртгэлийн хураамж: $260</span>
                      <span>Материалын хураамж: $650</span>
                    </div>
                  </div>
                </div>

                {/* Bridge International College */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-orange-500/20 border-b border-white/10 flex items-center gap-2">
                    <Star className="w-4 h-4 text-orange-400" />
                    <h3 className="text-white font-bold">Bridge International College — NZCEL хөтөлбөр</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left px-5 py-3 text-white/60">Хөтөлбөр</th>
                          <th className="text-left px-5 py-3 text-white/60">Хуваарь</th>
                          <th className="text-right px-5 py-3 text-white/60">Хугацаа</th>
                          <th className="text-right px-5 py-3 text-white/60">Анхны үнэ</th>
                          <th className="text-right px-5 py-3 text-orange-400">Урамшуулал</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { prog: "NZCEL Level 4", schedule: "13:00–17:00 (хичээл) + 17:00–18:00 (дадлага)", dur: "16 долоо хоног", orig: "$7,800", promo: "$4,750" },
                          { prog: "NZCEL Level 5", schedule: "13:00–17:00 (хичээл) + 17:00–18:00 (дадлага)", dur: "16 долоо хоног", orig: "$8,400", promo: "$4,750" },
                          { prog: "NZCEL Level 5 (Онлайн)", schedule: "13:00–17:00 (хичээл) + 17:00–18:00 (дадлага)", dur: "16 долоо хоног", orig: "$8,400", promo: "$4,250" },
                        ].map((row) => (
                          <tr key={row.prog} className="border-b border-white/5">
                            <td className="px-5 py-3 text-white font-medium">{row.prog}</td>
                            <td className="px-5 py-3 text-white/50 text-xs">{row.schedule}</td>
                            <td className="px-5 py-3 text-white/70 text-right">{row.dur}</td>
                            <td className="px-5 py-3 text-white/40 line-through text-right">{row.orig}</td>
                            <td className="px-5 py-3 text-orange-400 font-bold text-right">{row.promo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-3 text-xs text-white/40 border-t border-white/5">
                    Бүртгэлийн хураамж: $260 | Материалын хураамж: $650 | Долоо хоногт 25 цаг
                  </div>
                </div>

                {/* English requirements */}
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-purple-500/20 border-b border-white/10">
                    <h3 className="text-white font-bold">Англи хэлний шаардлага (IELTS)</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left px-5 py-3 text-white/60">Сургалтын түвшин</th>
                          <th className="text-right px-5 py-3 text-white/60">IELTS оноо</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { level: "Гэрчилгээ Level 4 (1–2 жил)", ielts: "5.5 (5-аас доош band байж болохгүй)" },
                          { level: "Диплом Level 5–6 эсвэл Level 7 (1 жил)", ielts: "5.5–6.0 (5.5-аас доош band байж болохгүй)" },
                          { level: "Бакалавр Level 7 (3 жил)", ielts: "6.0 (5.5-аас доош band байж болохгүй)" },
                          { level: "Постград диплом Level 8 (1 жил)", ielts: "6.5 (6.0-аас доош band байж болохгүй)" },
                          { level: "Магистр Level 9 (1–2 жил)", ielts: "6.5 (6.0-аас доош band байж болохгүй)" },
                          { level: "PhD Level 10", ielts: "7.0 (6.5–7.0-аас доош band байж болохгүй)" },
                        ].map((row) => (
                          <tr key={row.level} className="border-b border-white/5">
                            <td className="px-5 py-3 text-white">{row.level}</td>
                            <td className="px-5 py-3 text-purple-400 font-bold text-right">{row.ielts}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── 2. ВИЗНИЙ ХУРААМЖ ── */}
            {activeTab === "visa-fees" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Визний хураамжийн мэдээлэл</h2>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-200/90 text-sm">
                    INZ хураамж нь урьдчилан мэдэгдэлгүй өөрчлөгдөж болно. GST (15%) орохгүй. Эмнэлэг, зан чанарын шалгалт, нарийн асуудлын хураамж тусдаа.
                  </p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-[#00A86B]/20 border-b border-white/10">
                    <h3 className="text-white font-bold">Визний хураамж (NZD)</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left px-5 py-3 text-white/60">Визний төрөл</th>
                          <th className="text-right px-5 py-3 text-white/60">INZ хураамж</th>
                          <th className="text-right px-5 py-3 text-[#00A86B]">LIA (зуучлалын) хураамж</th>
                          <th className="text-right px-5 py-3 text-white font-semibold">Нийт</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { type: "Оюутны виз", inz: "850", lia: "1,000", total: "1,850", highlight: true },
                          { type: "Хамтрагч виз (Partner Visa)", inz: "1,630", lia: "1,500", total: "3,130" },
                          { type: "Дагалдах хүүхдийн оюутны/аяллын виз", inz: "850 / 441", lia: "750", total: "1,600 / 1,191" },
                          { type: "Оюутны визний сунгалт", inz: "850", lia: "500", total: "1,350" },
                          { type: "Суралцсаны дараах ажлын виз", inz: "1,670", lia: "500", total: "2,170", highlight: true },
                          { type: "Аяллын виз", inz: "441", lia: "750", total: "1,191" },
                          { type: "Нөхцөл өөрчлөлт (onshore)", inz: "325", lia: "750", total: "1,075" },
                          { type: "Section 61", inz: "—", lia: "1,500", total: "1,500+" },
                          { type: "Ажил олгогчийн баталгаажуулалт", inz: "775", lia: "1,250", total: "2,025" },
                          { type: "Ажлын байрны шалгалт (Job Check)", inz: "735", lia: "1,250", total: "1,985" },
                          { type: "Баталгаажсан ажил олгогчийн виз (AEWV)", inz: "1,540", lia: "2,000", total: "3,540", highlight: true },
                          { type: "Хамтрагчийн байнгын оршин суух", inz: "5,360", lia: "3,000", total: "8,360" },
                          { type: "Green List / WTR байнгын оршин суух", inz: "6,450", lia: "3,000", total: "9,450", highlight: true },
                          { type: "SMC байнгын оршин суух (Үндсэн өргөдөл гаргагч)", inz: "6,450", lia: "4,500", total: "10,950", highlight: true },
                          { type: "SMC (Хамтрагч)", inz: "—", lia: "2,000", total: "2,000+" },
                          { type: "SMC (Хүүхэд)", inz: "—", lia: "1,000", total: "1,000+" },
                        ].map((row) => (
                          <tr key={row.type} className={`border-b border-white/5 ${row.highlight ? "bg-[#00A86B]/5" : ""}`}>
                            <td className="px-5 py-3 text-white">{row.type}</td>
                            <td className="px-5 py-3 text-white/60 text-right">{row.inz}</td>
                            <td className="px-5 py-3 text-[#00A86B] text-right">{row.lia}</td>
                            <td className="px-5 py-3 text-white font-bold text-right">{row.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-3 text-xs text-white/40 border-t border-white/5">
                    * GST 15% орохгүй. ** INZ хураамж урьдчилан мэдэгдэлгүй өөрчлөгдөж болно. Эмнэлэг, зан чанарын шалгалт, нарийн асуудлын хураамж тусдаа.
                  </div>
                </div>
              </div>
            )}

            {/* ── 3. СУРГУУЛИУД ── */}
            {activeTab === "schools" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Сургуулиудын мэдээлэл</h2>
                <p className="text-white/60">United Dream Mongolia нь RBS Intellect-тэй хамтран Шинэ Зеландын 50+ сургуульд элсүүлэх боломжтой.</p>

                {/* Universities */}
                <div>
                  <h3 className="text-lg font-bold text-[#00A86B] mb-4 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" /> Их сургуулиуд
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { name: "University of Auckland", city: "Окленд", rank: "#65", bachelor: "NZD 29,000–38,000/жил", master: "NZD 35,000–55,000/жил", note: "Шинэ Зеландын тэргүүлэх их сургууль" },
                      { name: "Victoria University of Wellington", city: "Веллингтон", rank: "#240", bachelor: "NZD 25,000–35,000/жил", master: "NZD 30,000–45,000/жил", note: "Нийслэлд байрлах, хуулийн болон нийгмийн шинжлэх ухаанаараа алдартай" },
                      { name: "University of Canterbury", city: "Крайстчёрч", rank: "#261", bachelor: "NZD 22,000–32,000/жил", master: "NZD 28,000–42,000/жил", note: "Инженерчлэл, байгалийн шинжлэх ухаанаараа тэргүүлдэг" },
                      { name: "Massey University", city: "Окленд / Палмерстон Норт / Веллингтон", rank: "#230", bachelor: "NZD 22,000–32,000/жил", master: "NZD 25,000–40,000/жил", note: "Олон кампустай, онлайн сургалтаараа алдартай" },
                      { name: "University of Waikato", city: "Хамилтон", rank: "#281", bachelor: "NZD 20,000–30,000/жил", master: "NZD 25,000–38,000/жил", note: "Менежмент, хуулийн чиглэлээр хүчтэй" },
                      { name: "University of Otago", city: "Данедин", rank: "#197", bachelor: "NZD 22,000–33,000/жил", master: "NZD 28,000–45,000/жил", note: "Эмнэлгийн шинжлэх ухаанаараа дэлхийд алдартай" },
                      { name: "Lincoln University", city: "Линкольн (Крайстчёрч)", rank: "#407", bachelor: "NZD 20,000–28,000/жил", master: "NZD 24,000–35,000/жил", note: "Хөдөө аж ахуй, байгаль орчны шинжлэх ухаанаараа тэргүүлдэг" },
                      { name: "AUT (Auckland University of Technology)", city: "Окленд", rank: "#410", bachelor: "NZD 25,000–35,000/жил", master: "NZD 28,000–40,000/жил", note: "Технологи, бизнес, эрүүл мэндийн чиглэлээр хүчтэй" },
                    ].map((s) => (
                      <div key={s.name} className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-[#00A86B]/40 transition-colors">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-white font-bold text-base">{s.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <MapPin className="w-3.5 h-3.5 text-white/40" />
                              <span className="text-white/50 text-xs">{s.city}</span>
                            </div>
                          </div>
                          <div className="bg-[#00A86B]/20 text-[#00A86B] text-xs font-bold px-2.5 py-1 rounded-lg">QS {s.rank}</div>
                        </div>
                        <p className="text-white/50 text-xs mb-3 italic">{s.note}</p>
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-xs">
                            <span className="text-white/50">Бакалавр:</span>
                            <span className="text-[#00A86B] font-semibold">{s.bachelor}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-white/50">Магистр:</span>
                            <span className="text-[#00A86B] font-semibold">{s.master}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ITPs */}
                <div>
                  <h3 className="text-lg font-bold text-blue-400 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5" /> Технологийн дээд сургуулиуд (ITP)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {[
                      { name: "Weltec & Whiteria", city: "Веллингтон" },
                      { name: "Manukau Institute of Technology", city: "Окленд" },
                      { name: "Otago Polytechnic", city: "Окленд / Данедин" },
                      { name: "NMIT", city: "Нельсон" },
                      { name: "WITT", city: "Нью Плимут" },
                      { name: "SIT", city: "Инверкаргилл" },
                      { name: "Toi Ohomai", city: "Роторуа / Таурангa" },
                      { name: "EIT", city: "Нэйпир" },
                      { name: "UCOL", city: "Палмерстон Норт" },
                      { name: "Ara Institute of Canterbury", city: "Крайстчёрч" },
                      { name: "Wintec", city: "Хамилтон" },
                      { name: "Northtec", city: "Вангарей" },
                    ].map((s) => (
                      <div key={s.name} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:border-blue-400/40 transition-colors">
                        <div className="text-white text-sm font-medium">{s.name}</div>
                        <div className="flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3 text-white/30" />
                          <span className="text-white/40 text-xs">{s.city}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* PTEs */}
                <div>
                  <h3 className="text-lg font-bold text-orange-400 mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5" /> Хувийн сургалтын байгууллагууд (PTE) — Хэлний сургуулиуд
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {[
                      "ICL (International College of Language)",
                      "Bridge International College",
                      "Whitecliff College",
                      "UP Education",
                      "Kaplan",
                      "Media Design School",
                      "Le Cordon Bleu",
                      "AIS St Helens",
                      "NZLC",
                      "AEA English School",
                      "Crown English School",
                      "Zealive English School",
                      "Rotorua English Language Academy",
                      "NZSE",
                      "FutureSkills",
                      "AcademyEX",
                    ].map((name) => (
                      <div key={name} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:border-orange-400/40 transition-colors">
                        <div className="text-white text-xs font-medium">{name}</div>
                        <div className="text-white/40 text-xs mt-0.5">Окленд</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── 4. БАНКНЫ БАТАЛГАА ── */}
            {activeTab === "bank" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Банкны баталгаа</h2>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-white font-bold text-lg mb-4">INZ-ийн санхүүгийн шаардлага</h3>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <CheckCircle className="w-5 h-5 text-[#00A86B] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-white font-semibold">Амьдрах зардал: NZD 20,000/жил (заавал)</div>
                        <div className="text-white/50 text-sm">INZ-ийн тогтоосон доод хэмжээ. Энэ нь зөвхөн амьдрах зардал — сургалтын төлбөр тусдаа.</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle className="w-5 h-5 text-[#00A86B] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-white font-semibold">Сургалтын төлбөр: Бүрэн хэмжээгээр</div>
                        <div className="text-white/50 text-sm">Мэдүүлж буй хөтөлбөрийн нэг жилийн сургалтын төлбөрийг банкны дансанд байлгах шаардлагатай.</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle className="w-5 h-5 text-[#00A86B] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-white font-semibold">6 сарын банкны хуулга</div>
                        <div className="text-white/50 text-sm">Сүүлийн 6 сарын банкны хуулга шаардагдана. Тогтмол орлого, тогтвортой үлдэгдэл харуулах нь чухал.</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle className="w-5 h-5 text-[#00A86B] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-white font-semibold">Хадгаламж / Хугацаатай хадгаламж</div>
                        <div className="text-white/50 text-sm">Хугацаатай хадгаламж (Fixed Deposit) болон хадгаламжийн данс хүлээн зөвшөөрөгдөнө.</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-white font-bold text-lg mb-4">Ивээн тэтгэгчийн (Sponsor) баримт бичиг</h3>
                  <p className="text-white/60 text-sm mb-4">Хэрэв эцэг эх эсвэл гуравдагч этгээд санхүүжүүлж байгаа бол дараах баримт бичиг шаардагдана:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      "Ивээн тэтгэгчийн иргэний үнэмлэх / паспортын хуулбар",
                      "Сүүлийн 3 жилийн орлогын татварын тайлан",
                      "Цалингийн ажилтан бол: Цалингийн тодорхойлолт, ажлын гэрчилгээ, банкны хуулга",
                      "Бизнес эрхлэгч бол: Бизнесийн бүртгэлийн гэрчилгээ, татварын тайлан, банкны хуулга",
                      "Сүүлийн 6 сарын хадгаламжийн данс",
                      "Хугацаатай хадгаламжийн баримт",
                      "Боловсролын зээл (байгаа бол) + барьцааны баримт",
                    ].map((item) => (
                      <div key={item} className="flex gap-2.5">
                        <CheckCircle className="w-4 h-4 text-[#00A86B] flex-shrink-0 mt-0.5" />
                        <span className="text-white/70 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <p className="text-amber-200/80 text-xs">Монгол хэлнээс бусад хэлээр бичигдсэн баримт бичгийг Англи хэлэнд орчуулж, нотариатаар баталгаажуулах шаардлагатай.</p>
                  </div>
                </div>

                <div className="bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-xl p-5">
                  <h3 className="text-[#00A86B] font-bold mb-3">Жишээ тооцоолол (Бакалавр)</h3>
                  <div className="space-y-2 text-sm">
                    {[
                      { label: "Сургалтын төлбөр (жилд)", value: "NZD 25,000" },
                      { label: "Амьдрах зардал (INZ шаардлага)", value: "NZD 20,000" },
                      { label: "Нийт банкны дансанд байх ёстой дүн", value: "NZD 45,000+", bold: true },
                    ].map((row) => (
                      <div key={row.label} className="flex justify-between">
                        <span className="text-white/60">{row.label}</span>
                        <span className={row.bold ? "text-[#00A86B] font-bold text-base" : "text-white"}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── 5. БҮРДҮҮЛЭХ МАТЕРИАЛ ── */}
            {activeTab === "documents" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Бүрдүүлэх материал</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#00A86B]" /> Үндсэн баримт бичиг
                    </h3>
                    <div className="space-y-2.5">
                      {[
                        "Сургуулийн өргөдлийн маягт (хамаарах тохиолдолд)",
                        "Нотариатаар баталгаажсан паспортын хуулбар",
                        "Англи хэлний мэдлэгийн баталгаа (IELTS / PTE)",
                        "Нотариатаар баталгаажсан боловсролын баримт бичиг (10-р ангиас эхлэн)",
                        "Ажлын туршлагын баримт бичиг (байгаа бол)",
                        "Үүрэг хариуцлагыг тусгасан шинэчлэгдсэн CV",
                        "Завсарлагааны нотлох баримт (байгаа бол)",
                        "Зорилгын тайлбар бичиг (SOP) — сургуулиас шаардсан тохиолдолд",
                      ].map((item) => (
                        <div key={item} className="flex gap-2.5">
                          <CheckCircle className="w-4 h-4 text-[#00A86B] flex-shrink-0 mt-0.5" />
                          <span className="text-white/70 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-400" /> Визний мэдүүлгийн баримт бичиг
                    </h3>
                    <div className="space-y-2.5">
                      {[
                        "INZ 1226 — Оюутны визний мэдүүлгийн маягт (өргөдөл гаргагч гарын үсэг зурсан)",
                        "INZ 1014 — Санхүүгийн баталгааны маягт (Энэтхэгийн ивээн тэтгэгч эсвэл AEWV эзэмшигчдэд)",
                        "INZ 1025 — NZ-ийн оршин суугч / иргэн ивээн тэтгэгч байгаа тохиолдолд",
                        "Бүх хуудсыг багтаасан паспортын хуулбар (хуучин паспортуудыг оруулаад)",
                        "Эрх бүхий эмчийн E-medicals (мэдүүлэхийн өмнөх 3 сарын хугацаанд)",
                        "Цагдаагийн тодорхойлолт (PCC) — мэдүүлэхийн өмнөх 6 сарын хугацаанд",
                        "IELTS / PTE оноог баталгаажуулах гэрчилгээ",
                        "Паспортын хэмжээний зураг (JPEG формат)",
                        "Сургуулийн элсэлтийн баталгаа (OOP) + төлбөрийн баримт",
                        "COVID вакцинжуулалтын гэрчилгээ",
                        "Шинэчлэгдсэн CV",
                      ].map((item) => (
                        <div key={item} className="flex gap-2.5">
                          <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/70 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Process steps */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-white font-bold text-lg mb-6">Мэдүүлэх алхамууд</h3>
                  <div className="space-y-4">
                    {[
                      { step: "1", title: "Профайл үнэлгээ", desc: "CV болон боловсролын баримт бичгээ манай зөвлөхөд илгээнэ. Тохиромжтой хөтөлбөр, сургуулийг тодорхойлно." },
                      { step: "2", title: "Хөтөлбөр сонгох", desc: "RBS Intellect-тэй хамтран тохиромжтой хөтөлбөр, сургуулийг сонгоно." },
                      { step: "3", title: "Элсэлтийн баталгаа авах", desc: "Сургуульд өргөдөл гаргаж, элсэлтийн баталгаа (Offer of Place) авна." },
                      { step: "4", title: "Виз мэдүүлэх", desc: "Бүрдүүлэх материалын дагуу визний баримт бичгийг бүрдүүлж, INZ-д мэдүүлнэ." },
                      { step: "5", title: "AIP & E-Visa авах", desc: "Зарчмын зөвшөөрөл (AIP) авсны дараа сургалтын төлбөрийг төлж, E-Visa авна." },
                    ].map((s) => (
                      <div key={s.step} className="flex gap-4">
                        <div className="w-9 h-9 rounded-full bg-[#00A86B] flex items-center justify-center text-white font-black flex-shrink-0">{s.step}</div>
                        <div>
                          <div className="text-white font-semibold">{s.title}</div>
                          <div className="text-white/50 text-sm mt-0.5">{s.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── 6. ГЭР БҮЛЭЭРЭЭ ── */}
            {activeTab === "family" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Гэр бүлээрээ явах боломж</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#00A86B]" /> Хамтрагч (Partner) визний боломж
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-lg p-4">
                        <div className="text-[#00A86B] font-bold mb-2">✅ Нээлттэй ажлын виз авах боломжтой</div>
                        <p className="text-white/70 text-sm">Оюутан <strong>Level 9 (Магистр)</strong> эсвэл <strong>Green List</strong>-ийн мэргэжлийн хөтөлбөрт суралцаж байгаа тохиолдолд хамтрагч нь нээлттэй ажлын виз авна.</p>
                      </div>
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                        <div className="text-amber-400 font-bold mb-2">⚠️ Зөвхөн аяллын виз</div>
                        <p className="text-white/70 text-sm">Бусад хөтөлбөрт (Level 9-аас доош, Green List-д ороогүй) суралцаж байгаа тохиолдолд хамтрагч нь зөвхөн аяллын виз авна — ажиллах эрхгүй.</p>
                      </div>
                      <div className="text-sm text-white/60">
                        <strong className="text-white">Хамтрагчийн визний хураамж:</strong> INZ NZD 1,630 + LIA NZD 1,500 = <strong className="text-[#00A86B]">NZD 3,130</strong>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-400" /> Хүүхдийн визний боломж
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <div className="text-blue-400 font-bold mb-2">5–19 насны хүүхэд</div>
                        <p className="text-white/70 text-sm">Хамтрагч нь нээлттэй ажлын визтэй бол хүүхэд нь <strong>дотоодын оюутны виз</strong> авах боломжтой — улсын сургуулийн төлбөр үнэгүй болно.</p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                        <div className="text-white/70 font-bold mb-2">5 хүртэлх насны хүүхэд</div>
                        <p className="text-white/50 text-sm">Аяллын виз авна.</p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                        <div className="text-white/70 font-bold mb-2">Хамтрагч аяллын визтэй бол</div>
                        <p className="text-white/50 text-sm">Хүүхэд нь аяллын виз авна — сургуульд суралцахын тулд тусдаа оюутны виз шаардагдана.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="text-white font-bold mb-4">Гэр бүлийн нийт зардлын жишээ тооцоолол (Магистр)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left px-4 py-2 text-white/60">Зардлын төрөл</th>
                          <th className="text-right px-4 py-2 text-white/60">Дүн (NZD)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { item: "Магистрын сургалтын төлбөр (жилд)", amount: "38,000–65,000" },
                          { item: "Оюутны визний хураамж", amount: "1,850" },
                          { item: "Хамтрагчийн визний хураамж", amount: "3,130" },
                          { item: "Хүүхдийн визний хураамж (1 хүүхэд)", amount: "1,600" },
                          { item: "Амьдрах зардал (оюутан)", amount: "20,000/жил" },
                          { item: "Нэмэлт амьдрах зардал (хамтрагч)", amount: "10,000–15,000/жил" },
                          { item: "Нэмэлт амьдрах зардал (хүүхэд тус бүр)", amount: "5,000–10,000/жил" },
                          { item: "Эмнэлгийн даатгал", amount: "600–800/жил" },
                        ].map((row) => (
                          <tr key={row.item} className="border-b border-white/5">
                            <td className="px-4 py-2.5 text-white/70">{row.item}</td>
                            <td className="px-4 py-2.5 text-[#00A86B] font-semibold text-right">{row.amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── 7. АЖИЛЛАХ ЭРХ ── */}
            {activeTab === "work" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Ажиллах эрх — Сургалтын төрлөөр</h2>

                <div className="space-y-4">
                  {[
                    {
                      level: "Хэлний курс (14 долоо хоногоос доош)",
                      work: "Ажиллах эрхгүй",
                      color: "red",
                      detail: "14 долоо хоногоос богино хэлний курсийн оюутнууд Шинэ Зеландад ажиллах эрхгүй.",
                    },
                    {
                      level: "Хэлний курс (14+ долоо хоног)",
                      work: "Хязгаарлагдмал ажиллах эрх",
                      color: "yellow",
                      detail: "14 долоо хоног ба түүнээс дээш хугацааны хэлний курсийн оюутнуудад хязгаарлагдмал ажиллах эрх олгогдож болно. INZ-тэй тодруулах шаардлагатай.",
                    },
                    {
                      level: "Гэрчилгээ / Диплом (Level 3–6)",
                      work: "7 хоногт 20 цаг",
                      color: "green",
                      detail: "Level 3–6 хөтөлбөрт суралцаж буй оюутнууд долоо хоногт 20 цаг хүртэл ажиллах боломжтой.",
                    },
                    {
                      level: "Бакалавр (Level 7, 3 жил)",
                      work: "7 хоногт 25 цаг (2025.11.01-ээс)",
                      color: "green",
                      detail: "2025 оны 11 сарын 1-ний өдрөөс эхлэн дээд боловсролын оюутнууд долоо хоногт 25 цаг хүртэл ажиллах боломжтой болсон. Цагийн хөлс: NZD 23.50/цаг.",
                    },
                    {
                      level: "Постград диплом / Магистр (Level 8–9)",
                      work: "7 хоногт 25 цаг (2025.11.01-ээс)",
                      color: "green",
                      detail: "Дээд боловсролын оюутнуудад долоо хоногт 25 цаг хүртэл ажиллах боломжтой. Цагийн хөлс: NZD 23.50/цаг.",
                    },
                    {
                      level: "Докторантур (PhD)",
                      work: "Бүтэн цагийн ажиллах эрх",
                      color: "green",
                      detail: "PhD оюутнууд бүтэн цагаар ажиллах боломжтой.",
                    },
                  ].map((item) => (
                    <div key={item.level} className={`bg-white/5 border rounded-xl p-5 ${
                      item.color === "red" ? "border-red-500/30" :
                      item.color === "yellow" ? "border-amber-500/30" :
                      "border-[#00A86B]/30"
                    }`}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-white font-bold">{item.level}</h3>
                          <p className="text-white/50 text-sm mt-1">{item.detail}</p>
                        </div>
                        <div className={`px-3 py-1.5 rounded-lg text-sm font-bold whitespace-nowrap flex-shrink-0 ${
                          item.color === "red" ? "bg-red-500/20 text-red-400" :
                          item.color === "yellow" ? "bg-amber-500/20 text-amber-400" :
                          "bg-[#00A86B]/20 text-[#00A86B]"
                        }`}>
                          {item.work}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-xl p-5">
                  <h3 className="text-[#00A86B] font-bold mb-3">Суралцсаны дараах ажлын виз (Post-Study Work Visa)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left px-4 py-2 text-white/60">Хөтөлбөр</th>
                          <th className="text-right px-4 py-2 text-white/60">Ажлын визний хугацаа</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { prog: "30+ долоо хоног, Level 6 Барилга (2 жил)", work: "2 жил" },
                          { prog: "30+ долоо хоног (1 жил)", work: "1 жил" },
                          { prog: "Level 5 (5.5) + Level 7 (2 жил)", work: "2 жил" },
                          { prog: "90+ долоо хоног (3 жил)", work: "3 жил" },
                          { prog: "Постград диплом Level 8 (1 жил)", work: "1 жил" },
                          { prog: "Магистр Level 9 (1–2 жил)", work: "3 жил", highlight: true },
                          { prog: "PhD Level 10", work: "3 жил", highlight: true },
                          { prog: "Бакалавр Level 7 (3 жил)", work: "3 жил", highlight: true },
                          { prog: "Green List мэргэжилтэй Level 7", work: "3 жил", highlight: true },
                        ].map((row) => (
                          <tr key={row.prog} className={`border-b border-white/5 ${row.highlight ? "bg-[#00A86B]/5" : ""}`}>
                            <td className="px-4 py-2.5 text-white/70">{row.prog}</td>
                            <td className="px-4 py-2.5 text-[#00A86B] font-bold text-right">{row.work}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── 8. ХҮҮХЭД ДАГАЛДУУЛАХ ── */}
            {activeTab === "children" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Хүүхдээ дагалдуулан явах</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-bold mb-4">Хүүхдийн сургуулийн төлбөр</h3>
                    <div className="space-y-4">
                      <div className="bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-lg p-4">
                        <div className="text-[#00A86B] font-bold mb-2">✅ Үнэгүй сургуулийн боломж</div>
                        <p className="text-white/70 text-sm">Хамтрагч нь <strong>нээлттэй ажлын визтэй</strong> бол 5–19 насны хүүхэд <strong>дотоодын оюутны виз</strong> авах боломжтой. Энэ тохиолдолд улсын сургуулийн төлбөр <strong>үнэгүй</strong>.</p>
                      </div>
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                        <div className="text-amber-400 font-bold mb-2">⚠️ Олон улсын оюутны төлбөр</div>
                        <p className="text-white/70 text-sm">Хамтрагч аяллын визтэй бол хүүхэд нь олон улсын оюутны виз авах ёстой бөгөөд <strong>NZD 15,000–25,000/жил</strong> (улсын сургууль) төлбөр шаардагдана.</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-bold mb-4">Насны ангиллаар</h3>
                    <div className="space-y-3">
                      {[
                        { age: "5 хүртэлх нас", visa: "Аяллын виз", note: "Сургуульд суралцах эрхгүй", color: "amber" },
                        { age: "5–19 нас (хамтрагч ажлын визтэй)", visa: "Дотоодын оюутны виз", note: "Улсын сургуулийн төлбөр үнэгүй", color: "green" },
                        { age: "5–19 нас (хамтрагч аяллын визтэй)", visa: "Олон улсын оюутны виз", note: "NZD 15,000–25,000/жил", color: "amber" },
                      ].map((item) => (
                        <div key={item.age} className={`border rounded-lg p-4 ${
                          item.color === "green" ? "border-[#00A86B]/30 bg-[#00A86B]/5" : "border-amber-500/30 bg-amber-500/5"
                        }`}>
                          <div className="text-white font-semibold text-sm">{item.age}</div>
                          <div className={`text-sm font-bold mt-1 ${item.color === "green" ? "text-[#00A86B]" : "text-amber-400"}`}>{item.visa}</div>
                          <div className="text-white/50 text-xs mt-0.5">{item.note}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="text-white font-bold mb-4">Хүүхдийн визний хураамж</h3>
                  <div className="space-y-2 text-sm">
                    {[
                      { type: "Дагалдах хүүхдийн оюутны виз", inz: "NZD 850", lia: "NZD 750", total: "NZD 1,600" },
                      { type: "Дагалдах хүүхдийн аяллын виз", inz: "NZD 441", lia: "NZD 750", total: "NZD 1,191" },
                    ].map((row) => (
                      <div key={row.type} className="flex flex-wrap gap-2 items-center justify-between border-b border-white/5 pb-2">
                        <span className="text-white/70">{row.type}</span>
                        <div className="flex gap-4 text-xs">
                          <span className="text-white/40">INZ: {row.inz}</span>
                          <span className="text-[#00A86B]">LIA: {row.lia}</span>
                          <span className="text-white font-bold">Нийт: {row.total}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── 9. АМЬДРАХ ЗАРДАЛ ── */}
            {activeTab === "living" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Амьдрах зардал</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <Home className="w-5 h-5 text-[#00A86B]" /> Ганцаараа (жилд, NZD)
                    </h3>
                    <div className="space-y-3">
                      {[
                        { item: "INZ-ийн доод амьдрах зардал", amount: "20,000", required: true },
                        { item: "Орон байр (хуваалцсан)", amount: "10,000–15,000" },
                        { item: "Хоол хүнс", amount: "6,000–9,000" },
                        { item: "Тээвэр", amount: "2,000–3,500" },
                        { item: "Эмнэлгийн даатгал", amount: "600–800" },
                        { item: "Утас, интернет", amount: "600–1,200" },
                        { item: "Бусад зардал", amount: "2,000–4,000" },
                      ].map((row) => (
                        <div key={row.item} className="flex justify-between items-center">
                          <span className={`text-sm ${row.required ? "text-white font-semibold" : "text-white/60"}`}>
                            {row.item} {row.required && <span className="text-[#00A86B] text-xs">(заавал)</span>}
                          </span>
                          <span className="text-[#00A86B] font-bold text-sm">{row.amount}</span>
                        </div>
                      ))}
                      <div className="border-t border-white/10 pt-3 flex justify-between">
                        <span className="text-white font-bold">Нийт (жилд)</span>
                        <span className="text-[#00A86B] font-black text-lg">~32,000–38,000</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-400" /> Гэр бүлээрээ (жилд, NZD)
                    </h3>
                    <div className="space-y-3">
                      {[
                        { item: "Оюутны амьдрах зардал", amount: "32,000–38,000" },
                        { item: "Хамтрагчийн нэмэлт зардал", amount: "+10,000–15,000" },
                        { item: "Хүүхэд тус бүрийн нэмэлт зардал", amount: "+5,000–10,000" },
                      ].map((row) => (
                        <div key={row.item} className="flex justify-between items-center">
                          <span className="text-white/60 text-sm">{row.item}</span>
                          <span className="text-blue-400 font-bold text-sm">{row.amount}</span>
                        </div>
                      ))}
                      <div className="border-t border-white/10 pt-3">
                        <div className="flex justify-between mb-1">
                          <span className="text-white font-bold">Хосоор (жилд)</span>
                          <span className="text-blue-400 font-black text-lg">~42,000–53,000</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white font-bold">Хосоор + 1 хүүхэд</span>
                          <span className="text-blue-400 font-black text-lg">~47,000–63,000</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="text-white font-bold mb-4">Хотоор харьцуулсан амьдрах зардал</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { city: "Окленд", cost: "Хамгийн өндөр", note: "Шинэ Зеландын хамгийн том хот. Орон байрны үнэ өндөр.", color: "red" },
                      { city: "Веллингтон", cost: "Дунд зэрэг", note: "Нийслэл хот. Оклендоос арай хямд.", color: "yellow" },
                      { city: "Крайстчёрч / Данедин", cost: "Харьцангуй хямд", note: "Жижиг хотууд. Амьдрах зардал доогуур.", color: "green" },
                    ].map((c) => (
                      <div key={c.city} className={`border rounded-xl p-4 ${
                        c.color === "red" ? "border-red-500/30 bg-red-500/5" :
                        c.color === "yellow" ? "border-amber-500/30 bg-amber-500/5" :
                        "border-[#00A86B]/30 bg-[#00A86B]/5"
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="w-4 h-4 text-white/40" />
                          <span className="text-white font-bold">{c.city}</span>
                        </div>
                        <div className={`text-sm font-bold mb-1 ${
                          c.color === "red" ? "text-red-400" :
                          c.color === "yellow" ? "text-amber-400" :
                          "text-[#00A86B]"
                        }`}>{c.cost}</div>
                        <p className="text-white/50 text-xs">{c.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── 10. БАЙНГЫН ОРШИН СУУХ ── */}
            {activeTab === "residence" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white">Байнгын оршин суух эрх авах боломжууд</h2>

                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <h3 className="text-white font-bold mb-4">Оршин суух эрхийн ялгаа</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                      <div className="text-blue-400 font-bold text-lg mb-2">Residence Visa (RV)</div>
                      <ul className="space-y-1.5 text-sm text-white/70">
                        <li>• Тодорхойгүй хугацаагаар оршин суух боломжтой</li>
                        <li>• Аялалын нөхцөл болон хугацаатай (2–4 жил)</li>
                        <li>• Уламжлалт PR-тэй адил</li>
                      </ul>
                    </div>
                    <div className="bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-xl p-4">
                      <div className="text-[#00A86B] font-bold text-lg mb-2">Permanent Residence Visa (PRV)</div>
                      <ul className="space-y-1.5 text-sm text-white/70">
                        <li>• Аялалын нөхцөлгүй, хугацаагүй оршин суух</li>
                        <li>• "Насан туршийн" — зөвхөн Шинэ Зеландад</li>
                        <li>• RV-ийн дараа авах боломжтой</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-white">Байнгын оршин суух эрх авах 3 үндсэн зам</h3>

                  <Accordion title="🟢 1. Green List — Шууд байнгын оршин суух" defaultOpen>
                    <div className="space-y-3">
                      <p>Green List нь Шинэ Зеландад хэрэгцээтэй мэргэжлүүдийн жагсаалт юм.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        <div className="bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-lg p-3">
                          <div className="text-[#00A86B] font-bold mb-1">Tier 1 — Шууд байнгын оршин суух</div>
                          <p className="text-white/60 text-xs">Green List Tier 1 мэргэжилтэй бол ажлын байртай тохиолдолд шууд байнгын оршин суух эрх хүсэх боломжтой.</p>
                        </div>
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                          <div className="text-blue-400 font-bold mb-1">Tier 2 — 2 жил ажилласны дараа</div>
                          <p className="text-white/60 text-xs">Green List Tier 2 мэргэжилтэй бол 2 жил ажилласны дараа байнгын оршин суух эрх хүсэх боломжтой.</p>
                        </div>
                      </div>
                      <p className="text-white/50 text-xs">Эмч, сувилагч, инженер, IT мэргэжилтэн, багш зэрэг мэргэжлүүд Green List-д ордог.</p>
                    </div>
                  </Accordion>

                  <Accordion title="🔵 2. Skilled Migrant Category (SMC) — Ур чадварт суурилсан систем">
                    <div className="space-y-3">
                      <p>SMC нь 6 шаардлагыг хангасан тохиолдолд байнгын оршин суух эрх хүсэх боломжийг олгодог.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                        {[
                          "55 хүртэлх насны (өргөдөл гаргах үед)",
                          "Green List мэргэжил эсвэл 6 оноо",
                          "IELTS 6.5 эсвэл Англи хэлний баталгаа",
                          "Эрүүл мэнд, зан чанарын цэвэр бүртгэл",
                          "Гэр бүл (хамтрагч + хүүхдүүд) оруулах боломжтой",
                          "Шинэ Зеландад ажлын байртай байх",
                        ].map((item) => (
                          <div key={item} className="flex gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                            <span className="text-white/70 text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Accordion>

                  <Accordion title="🟡 3. Суралцаад ажиллаад байнгын оршин суух авах зам">
                    <div className="space-y-3">
                      <p>Хамгийн түгээмэл зам:</p>
                      <div className="flex flex-wrap gap-2 items-center mt-2">
                        {[
                          "Оюутны виз",
                          "→",
                          "Суралцсаны дараах ажлын виз",
                          "→",
                          "AEWV (Ажлын виз)",
                          "→",
                          "Байнгын оршин суух",
                        ].map((step, i) => (
                          <span key={i} className={step === "→" ? "text-white/30 text-lg" : "bg-white/10 text-white text-xs px-3 py-1.5 rounded-lg font-medium"}>
                            {step}
                          </span>
                        ))}
                      </div>
                      <div className="mt-3 space-y-2">
                        <p className="text-white/70 text-sm"><strong className="text-white">Бакалавр (3 жил):</strong> Суралцсаны дараа 3 жилийн ажлын виз → Green List эсвэл SMC-ээр байнгын оршин суух</p>
                        <p className="text-white/70 text-sm"><strong className="text-white">Магистр (1–2 жил):</strong> Суралцсаны дараа 3 жилийн ажлын виз → Green List эсвэл SMC-ээр байнгын оршин суух</p>
                        <p className="text-white/70 text-sm"><strong className="text-white">PhD:</strong> Суралцсаны дараа 3 жилийн ажлын виз → Байнгын оршин суух</p>
                      </div>
                    </div>
                  </Accordion>

                  <Accordion title="🟠 4. Хамтрагчийн байнгын оршин суух (Partnership Residence)">
                    <div className="space-y-2">
                      <p>Шинэ Зеландын иргэн эсвэл оршин суугчтай гэрлэсэн буюу 12+ сарын хамтарсан амьдрал байгаа тохиолдолд байнгын оршин суух эрх хүсэх боломжтой.</p>
                      <div className="text-sm text-white/60 mt-2">
                        <strong className="text-white">Хураамж:</strong> INZ NZD 5,360 + LIA NZD 3,000 = <strong className="text-[#00A86B]">NZD 8,360</strong>
                      </div>
                    </div>
                  </Accordion>
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-r from-[#00A86B]/20 to-[#1E6FD9]/20 border border-[#00A86B]/30 rounded-xl p-6 text-center">
                  <Globe className="w-10 h-10 text-[#00A86B] mx-auto mb-3" />
                  <h3 className="text-white font-bold text-xl mb-2">Шинэ Зеланд руу нүүх мөрөөдлөө биелүүлэх үү?</h3>
                  <p className="text-white/60 text-sm mb-5">Манай мэргэжилтэнтэй зөвлөлдөж, таны нөхцөл байдалд тохирсон хамгийн тохиромжтой замыг тодорхойлно.</p>
                  <a
                    href="#" onClick={(e) => { e.preventDefault(); openConsultation(); }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#00A86B] text-white rounded-lg font-semibold hover:bg-[#009960] transition-colors"
                  >
                    Үнэгүй зөвлөгөө авах
                  </a>
                </div>
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Application Form Overlay ─────────────────────────────────────────────────────────── */}
      {showApplyForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: "rgba(10,22,40,0.97)" }}>
          <div className="container py-8 pb-20">
            {/* Close bar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇳🇿</span>
                <div>
                  <div className="text-white font-bold text-lg">Шинэ Зеланд Сургууль Анкет</div>
                  <div className="text-white/50 text-sm">New Zealand Student Application Form</div>
                </div>
              </div>
              <button
                onClick={() => { setShowApplyForm(false); setAppSubmitted(false); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all text-sm font-medium"
              >
                ✕ Хаах
              </button>
            </div>
            <motion.div key="apply" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.25 }}>             {appSubmitted ? (
                <div className="text-center py-20">
                  <CheckCircle className="w-20 h-20 text-[#00A86B] mx-auto mb-6" />
                  <h2 className="text-white text-3xl font-bold mb-4">Амжилттай илгэгдлээ!</h2>
                  <p className="text-white/70 text-lg mb-2">Таны анкет хүлээн авагдлаа.</p>
                  <p className="text-white/50">Манай мэргэжилтэн тантай удахгүй холбогдох болно.</p>
                  <button onClick={() => { setAppSubmitted(false); setShowApplyForm(false); }} className="mt-8 px-6 py-3 bg-[#00A86B] text-white rounded-lg font-semibold hover:bg-[#009960] transition-colors">
                    Нүүр рүү буцах
                  </button>
                </div>
              ) : (
                <form onSubmit={handleAppSubmit} className="space-y-8">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-[#00A86B]/20 to-[#1E6FD9]/20 border border-[#00A86B]/30 rounded-xl p-6">
                    <h2 className="text-white text-2xl font-bold mb-1">Шинэ Зеланд Сургууль Анкет</h2>
                    <p className="text-white/60 text-sm">New Zealand Student Application Form</p>
                    <p className="text-yellow-400/80 text-xs mt-2">★ Тэмдэгтэй талбаруудыг заавал бөглөнө үү.</p>
                  </div>

                  {/* Section 1: Personal Info */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="text-[#00A86B] font-bold text-lg mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> 1. Хувийн мэдээлэл / Applicant Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[{ key: "studentName", mn: "Овог нэр *", en: "Student name *" }, { key: "gender", mn: "Хүйс", en: "Gender" }, { key: "dob", mn: "Төрсөн өдр *", en: "Date of Birth *" }, { key: "telephone", mn: "Утас дугаар", en: "Telephone" }, { key: "mobile", mn: "Гар утас *", en: "Mobile *" }, { key: "email", mn: "Имэйл *", en: "E-mail *" }, { key: "passportNumber", mn: "Паспортын дугаар *", en: "Passport Number *" }, { key: "passportStartDate", mn: "Паспорт олгосон огноо *", en: "Passport Start Date *" }, { key: "passportExpiryDate", mn: "Паспорт дуусгах огноо *", en: "Passport Expiry Date *" }].map(f => (
                        <div key={f.key}>
                          <label className="block text-white/70 text-xs mb-1">{f.mn} <span className="text-white/40">/ {f.en}</span></label>
                          <input
                            type={f.key === "email" ? "email" : "text"}
                            value={(appForm as any)[f.key]}
                            onChange={e => setAppForm(p => ({ ...p, [f.key]: e.target.value }))}
                            required={f.mn.includes("*")}
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#00A86B]"
                            placeholder={f.en}
                          />
                        </div>
                      ))}
                      <div className="md:col-span-2">
                        <label className="block text-white/70 text-xs mb-1">Гэрийн хаяг * <span className="text-white/40">/ Residential Address *</span></label>
                        <textarea value={appForm.residentialAddress} onChange={e => setAppForm(p => ({ ...p, residentialAddress: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#00A86B]" placeholder="Residential address" />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Next of Kin */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="text-[#00A86B] font-bold text-lg mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> 2. Ойр төрөл хүн / Next of Kin Details *</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[{ key: "kinName", mn: "Нэр", en: "Name" }, { key: "kinRelationship", mn: "Төрөл харьцаа", en: "Relationship" }, { key: "kinMobile", mn: "Гар утас", en: "Mobile" }, { key: "kinEmail", mn: "Имэйл", en: "Email" }].map(f => (
                        <div key={f.key}>
                          <label className="block text-white/70 text-xs mb-1">{f.mn} <span className="text-white/40">/ {f.en}</span></label>
                          <input type={f.key === "kinEmail" ? "email" : "text"} value={(appForm as any)[f.key]} onChange={e => setAppForm(p => ({ ...p, [f.key]: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#00A86B]" placeholder={f.en} />
                        </div>
                      ))}
                      <div className="md:col-span-2">
                        <label className="block text-white/70 text-xs mb-1">Хаяг <span className="text-white/40">/ Address</span></label>
                        <textarea value={appForm.kinAddress} onChange={e => setAppForm(p => ({ ...p, kinAddress: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#00A86B]" placeholder="Address" />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Education */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="text-[#00A86B] font-bold text-lg mb-4 flex items-center gap-2"><GraduationCap className="w-5 h-5" /> 3. Боловсролын мэдээлэл / Education Background</h3>
                    {education.map((ed, i) => (
                      <div key={i} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 p-3 bg-white/5 rounded-lg">
                        <div><label className="block text-white/60 text-xs mb-1">Tөгсөл / Year</label><input value={ed.year} onChange={e => setEducation(p => p.map((x, j) => j === i ? { ...x, year: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="2020" /></div>
                        <div><label className="block text-white/60 text-xs mb-1">Сургууль / Institution</label><input value={ed.institution} onChange={e => setEducation(p => p.map((x, j) => j === i ? { ...x, institution: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="Institution" /></div>
                        <div><label className="block text-white/60 text-xs mb-1">Мэргэжил / Programme</label><input value={ed.programme} onChange={e => setEducation(p => p.map((x, j) => j === i ? { ...x, programme: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="Programme" /></div>
                        <div><label className="block text-white/60 text-xs mb-1">Дундааж / GPA</label><input value={ed.gpa} onChange={e => setEducation(p => p.map((x, j) => j === i ? { ...x, gpa: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="GPA" /></div>
                      </div>
                    ))}
                    <button type="button" onClick={() => setEducation(p => [...p, { year: "", institution: "", programme: "", gpa: "" }])} className="text-[#00A86B] text-sm hover:underline">+ Мөр нэмэх / Add more</button>
                  </div>

                  {/* Section 4: English Proficiency */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="text-[#00A86B] font-bold text-lg mb-4 flex items-center gap-2"><Globe className="w-5 h-5" /> 4. Англи хэлний чадвар / English Language Proficiency</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="md:col-span-3">
                        <label className="block text-white/70 text-xs mb-1">Шалгалтын төрөл / Exam Type</label>
                        <select value={appForm.englishExamType} onChange={e => setAppForm(p => ({ ...p, englishExamType: e.target.value }))} className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00A86B]">
                          <option value="IELTS">IELTS</option>
                          <option value="PTE">PTE</option>
                          <option value="TOEFL">TOEFL</option>
                          <option value="N/A">Байхгүй / N/A</option>
                        </select>
                      </div>
                      {[{ key: "englishReading", mn: "Уншлах", en: "Reading" }, { key: "englishWriting", mn: "Бичлэх", en: "Writing" }, { key: "englishListening", mn: "Сонсгох", en: "Listening" }, { key: "englishSpeaking", mn: "Ярих", en: "Speaking" }, { key: "englishOverall", mn: "Нийт дүн", en: "Overall" }].map(f => (
                        <div key={f.key}>
                          <label className="block text-white/70 text-xs mb-1">{f.mn} / {f.en}</label>
                          <input value={(appForm as any)[f.key]} onChange={e => setAppForm(p => ({ ...p, [f.key]: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00A86B]" placeholder={f.en} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section 5: Work Experience */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="text-[#00A86B] font-bold text-lg mb-4 flex items-center gap-2"><Briefcase className="w-5 h-5" /> 5. Ажлын туршлага / Work Experience</h3>
                    {workExp.map((w, i) => (
                      <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 p-3 bg-white/5 rounded-lg">
                        <div><label className="block text-white/60 text-xs mb-1">Он хугацаа / Start &amp; End Date</label><input value={w.startEnd} onChange={e => setWorkExp(p => p.map((x, j) => j === i ? { ...x, startEnd: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="2020-2022" /></div>
                        <div><label className="block text-white/60 text-xs mb-1">Байгууллага / Organisation</label><input value={w.organisation} onChange={e => setWorkExp(p => p.map((x, j) => j === i ? { ...x, organisation: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="Organisation" /></div>
                        <div><label className="block text-white/60 text-xs mb-1">Албан тушаал / Job Title</label><input value={w.jobTitle} onChange={e => setWorkExp(p => p.map((x, j) => j === i ? { ...x, jobTitle: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="Job title" /></div>
                        <div><label className="block text-white/60 text-xs mb-1">Чиг үүрэг / Duties</label><input value={w.duties} onChange={e => setWorkExp(p => p.map((x, j) => j === i ? { ...x, duties: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="Duties" /></div>
                      </div>
                    ))}
                    <button type="button" onClick={() => setWorkExp(p => [...p, { startEnd: "", organisation: "", jobTitle: "", duties: "" }])} className="text-[#00A86B] text-sm hover:underline">+ Мөр нэмэх / Add more</button>
                  </div>

                  {/* Section 6: Referees */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="text-[#00A86B] font-bold text-lg mb-4 flex items-center gap-2"><Star className="w-5 h-5" /> 6. Лавлагчийн мэдээлэл / Referee Details</h3>
                    {referees.map((r, i) => (
                      <div key={i} className="mb-4">
                        <p className="text-white/50 text-xs mb-2">Referee {i + 1}</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-white/5 rounded-lg">
                          {[{ key: "name", mn: "Нэр", en: "Name" }, { key: "title", mn: "Цол", en: "Title" }, { key: "organisation", mn: "Байгууллага", en: "Organisation" }, { key: "contact", mn: "Холбооны делгэрэнгүй", en: "Contact details" }, { key: "mobile", mn: "Гар утас", en: "Mobile" }, { key: "email", mn: "Имэйл", en: "E-mail" }].map(f => (
                            <div key={f.key}>
                              <label className="block text-white/60 text-xs mb-1">{f.mn} / {f.en}</label>
                              <input value={(r as any)[f.key]} onChange={e => setReferees(p => p.map((x, j) => j === i ? { ...x, [f.key]: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder={f.en} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Section 7: Preferred Institutions */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="text-[#00A86B] font-bold text-lg mb-4 flex items-center gap-2"><Building2 className="w-5 h-5" /> 7. Хүснэх сургуулиуд / Preferred Institutions</h3>
                    {prefInst.map((p, i) => (
                      <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 p-3 bg-white/5 rounded-lg">
                        <div><label className="block text-white/60 text-xs mb-1">Сургууль / Institution</label><input value={p.institution} onChange={e => setPrefInst(prev => prev.map((x, j) => j === i ? { ...x, institution: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="Institution" /></div>
                        <div><label className="block text-white/60 text-xs mb-1">Мэргэжил / Programme</label><input value={p.programme} onChange={e => setPrefInst(prev => prev.map((x, j) => j === i ? { ...x, programme: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="Programme" /></div>
                        <div><label className="block text-white/60 text-xs mb-1">Эхлэх огноо / Start Date</label><input value={p.startDate} onChange={e => setPrefInst(prev => prev.map((x, j) => j === i ? { ...x, startDate: e.target.value } : x))} className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00A86B]" placeholder="Start date" /></div>
                      </div>
                    ))}
                  </div>

                  {/* Section 8: Documents Checklist */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="text-[#00A86B] font-bold text-lg mb-4 flex items-center gap-2"><FileText className="w-5 h-5" /> 8. Бүрдүүлэх бичиг баримт / Documents Checklist</h3>
                    <div className="space-y-3">
                      {[
                        { key: "passport", mn: "Паспортын хуулбар", en: "Passport copy" },
                        { key: "transcript", mn: "Боловсролын бичиг баримт", en: "Academic transcript" },
                        { key: "cv", mn: "Анамнамал (CV)", en: "Curriculum Vitae (CV)" },
                        { key: "sop", mn: "Зорилгоон тайлбар (SOP)", en: "Statement of Purpose (SOP)" },
                        { key: "english", mn: "Англи хэлний шалгалтын дүн", en: "English proficiency certificate" },
                        { key: "emergency", mn: "Яаралгах хүний мэдээлэл", en: "Emergency contact information" },
                      ].map(d => (
                        <label key={d.key} className="flex items-center gap-3 cursor-pointer group">
                          <input type="checkbox" checked={docChecks[d.key]} onChange={e => setDocChecks(p => ({ ...p, [d.key]: e.target.checked }))} className="w-4 h-4 rounded accent-[#00A86B]" />
                          <span className="text-white/80 text-sm group-hover:text-white transition-colors">{d.mn} <span className="text-white/40">/ {d.en}</span></span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="flex justify-center pb-8">
                    <button
                      type="submit"
                      disabled={submitApp.isPending}
                      className="px-10 py-4 bg-[#00A86B] text-white rounded-xl font-bold text-lg hover:bg-[#009960] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
                    >
                      {submitApp.isPending ? (
                        <><Clock className="w-5 h-5 animate-spin" /> Илгэж байна...</>
                      ) : (
                        <><CheckCircle className="w-5 h-5" /> Анкет илгээх / Submit Application</>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
